import { eachMonthOfInterval, format, isValid, parseISO } from 'date-fns';

namespace DateTimeHelpers {
  export const MINUTES_IN_HOUR = 60;
  export const MILLISECONDS_IN_MINUTE = 60000;
  export const MILLISECONDS_IN_HOUR = MILLISECONDS_IN_MINUTE * MINUTES_IN_HOUR;

  export const getHoursInMilliseconds = (milliseconds: number) =>
    Math.floor(milliseconds / MILLISECONDS_IN_HOUR);

  export const waitMs = (ms: number) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  // converts date to format yyyy-mm-dd
  export const dateToIsoDate = (date: Date | string | null): string | null => {
    if (!date) {
      return null;
    }
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }

    const parsedDate = new Date(date);
    // return null if parsing failed
    if (Number.isNaN(parsedDate.valueOf())) {
      return null;
    }
    // recurse after casting to date object
    return dateToIsoDate(parsedDate);
  };

  /**
   * Return list of months
   * localeName   : name of local, f.e. en-GB, default es-MX
   * monthFormat : short, numeric, long (Default)
   * https://stackoverflow.com/a/66626916
   */
  export const monthsForLocale = (
    localeName: string,
    monthFormat: 'short' | 'numeric' | 'long' = 'long'
  ) => {
    const { format } = new Intl.DateTimeFormat(localeName, {
      month: monthFormat,
    });
    return [...new Array(12).keys()].map((m) =>
      format(new Date(Date.UTC(2021, (m + 1) % 12)))
    );
  };

  /**
   * Uses the user's browser configured locale to return the name of the month number (0 indexed, ie. January is 0)
   * @param localeName "en-GB", "es-MX", etc.
   * @param monthNumber
   * @param monthFormat "short" returns "Jan", "numeric" returns "1", "long" returns "January". defaults to "long"
   * @returns
   */
  export const getMonthName = (
    localeName: string,
    monthNumber: number,
    monthFormat: 'short' | 'numeric' | 'long' = 'long'
  ) => {
    const { format } = new Intl.DateTimeFormat(localeName, {
      month: monthFormat,
    });
    return format(new Date(Date.UTC(2021, monthNumber)));
  };

  /**
   * Returns a list of [year, month] tuples for the given interval. The Months are 0-indexed to match RI, ie. January is 0.
   */
  export const getMonthsYearsInInterval = (start: Date, end: Date) => {
    const interval = { start, end };
    const dates = eachMonthOfInterval(interval);
    return dates.map(
      (date) => [date.getFullYear(), date.getMonth()] as [number, number]
    );
  };

  // removes milliseconds in iso date
  // https://stackoverflow.com/questions/34053715/how-to-output-date-in-javascript-in-iso-8601-without-milliseconds-and-with-z
  // 2023-07-07T15:22:00.102Z -> 2023-07-07T15:22:00Z
  export const dateToIso8601 = (date: Date | string) =>
    `${new Date(date).toISOString().split('.')[0]}Z`;

  export const subtractMonths = (date: Date, months: number) => {
    const oneMonthAgo = new Date(date);
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - months);
    return new Date(oneMonthAgo);
  };

  export const subtractHours = (
    date: Date | string | number,
    hours: number
  ) => {
    const hoursAgo = new Date(date);
    hoursAgo.setHours(hoursAgo.getHours() - hours);
    return new Date(hoursAgo);
  };

  export const dateToMonthDayYear = (date: Date | string) => {
    const d = new Date(date);
    const day = d.getDate();
    const month = d.toLocaleString(undefined, { month: 'long' });
    const year = d.getFullYear();

    return `${month} ${day} ${year}`;
  };

  export const formatHoursAndMinutes = (hours: number) => {
    const minutes = (hours % 1) * 60.0;
    return `${Math.floor(hours)} hrs ${Math.round(minutes)} mins`;
  };

  export const compareTimeInHours = (fromDate: Date, toDate: Date): number => {
    // Takes two dates and returns the difference between them in hours
    const timeDifferenceInMilliseconds = toDate.getTime() - fromDate.getTime();
    const hoursDifference = timeDifferenceInMilliseconds / (1000 * 60 * 60);
    // returns an hour rounded as a whole number, unless it's > 1 where it shows
    // to 1 decimal place
    return hoursDifference >= 1
      ? Math.round(hoursDifference)
      : Number(hoursDifference.toFixed(1));
  };

  export const compareTimeInMinutes = (fromDate: Date, toDate: Date) => {
    // Takes two dates and returns the difference between them in minutes
    const timeDifferenceInMilliseconds = toDate.getTime() - fromDate.getTime();
    const minutesDifference = timeDifferenceInMilliseconds / (1000 * 60);
    return minutesDifference;
  };

  /**
   * Extracts "YYYY-MM-DD" from a date string.
   * Returns null if the date is invalid or empty.
   * * @example toDateString("2026-03-21 10:16:00") -> "2026-03-21"
   */
  export const toDateString = (
    value: string | null | undefined
  ): string | null => {
    if (!value) return null;
    const date = parseISO(value);
    if (!isValid(date)) return null;
    return format(date, 'yyyy-MM-dd');
  };
}

export default DateTimeHelpers;

export const dateToUtcTimeString = (
  date: Date | number | string | null,
  returnDateOnly?: boolean // Optional argument to return only the date part
): string | null => {
  if (!date) {
    return null;
  }
  if (date instanceof Date) {
    try {
      const timeString = date.toISOString().split('T')[1].slice(0, 5);
      const dateString = date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'UTC',
      });

      if (returnDateOnly) {
        return dateString;
      }

      return `${dateString} ${timeString}`;
    } catch {
      return `00:00:00 Invalid Date`;
    }
  }

  const parsedDate = new Date(date);
  // return null if parsing failed
  if (Number.isNaN(parsedDate.valueOf())) {
    return null;
  }
  // recurse after casting to date object
  return dateToUtcTimeString(parsedDate, returnDateOnly);
};

// returns just date, respecting the user's locale
export const dateWithLocale = (
  date: Date | string | null,
  tzOverride?: string
): string | null => {
  if (!date) {
    return null;
  }
  if (date instanceof Date) {
    return date.toLocaleDateString(navigator.language, {
      timeZone: tzOverride,
    });
  }

  const parsedDate = new Date(date);
  // return null if parsing failed
  if (Number.isNaN(parsedDate.valueOf())) {
    return null;
  }
  // recurse after casting to date object
  return dateWithLocale(parsedDate, tzOverride);
};
